import { useEffect, useRef, useState } from 'react';
import { t } from '../i18n';
import './PWAInstallBanner.css';

const DISMISS_KEY = 'dancemance.pwa.install.dismissed';

function isDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function setDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    /* ignore */
  }
}

/**
 * Detect iOS/iPadOS Safari specifically.
 * - Old iOS: UA contains iPad/iPhone/iPod
 * - iPadOS 13+: UA says "Macintosh" but has touch points
 * - Must be Safari, not Chrome/Firefox/Edge (they can't install PWAs on iOS)
 * - Must not already be running as installed PWA
 */
function detectIosSafari(): boolean {
  const ua = navigator.userAgent;
  const isIosUA = /iP(hone|ad|od)/.test(ua);
  const isPadOs = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true;
  return (isIosUA || isPadOs) && isSafari && !isStandalone;
}

type Platform = 'ios' | 'android' | null;

export function PWAInstallBanner() {
  const [platform, setPlatform] = useState<Platform>(null);
  const [visible, setVisible] = useState(false);
  const deferredPrompt = useRef<any>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isDismissed()) return;

    // Already running as installed PWA — never show
    if (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true
    ) {
      return;
    }

    // ── iOS / iPadOS Safari ──────────────────────────────────────
    if (detectIosSafari()) {
      setPlatform('ios');
      timerRef.current = setTimeout(() => setVisible(true), 3500);
      return () => {
        if (timerRef.current) clearTimeout(timerRef.current);
      };
    }

    // ── Android / Chrome / Edge — capture beforeinstallprompt ────
    function onBeforeInstall(e: Event) {
      e.preventDefault();
      deferredPrompt.current = e;
      setPlatform('android');
      timerRef.current = setTimeout(() => setVisible(true), 3500);
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  function dismiss() {
    setDismissed();
    setVisible(false);
    if (timerRef.current) clearTimeout(timerRef.current);
  }

  async function install() {
    if (deferredPrompt.current) {
      try {
        deferredPrompt.current.prompt();
        const { outcome } = await deferredPrompt.current.userChoice;
        deferredPrompt.current = null;
        if (outcome === 'accepted') {
          dismiss();
          return;
        }
      } catch {
        /* prompt can only be called once */
      }
    }
    dismiss();
  }

  if (!visible || !platform) return null;

  return (
    <div className={`pwa-banner pwa-banner--${platform}`} role="complementary" aria-label={t('pwa.install.title')}>
      <div className="pwa-banner__icon" aria-hidden="true">
        <img src="/apple-touch-icon.png" alt="" width={44} height={44} />
      </div>
      <div className="pwa-banner__body">
        <p className="pwa-banner__title">{t('pwa.install.title')}</p>
        <p className="pwa-banner__msg">
          {platform === 'ios' ? t('pwa.install.ios') : t('pwa.install.android')}
        </p>
        {platform === 'ios' && (
          <p className="pwa-banner__ios-hint" aria-hidden="true">
            ⬆ &rarr; <strong>افزودن به صفحه اصلی</strong>
          </p>
        )}
      </div>
      <div className="pwa-banner__actions">
        {platform === 'android' && (
          <button
            id="pwa-install-btn"
            type="button"
            className="pwa-banner__install-btn"
            onClick={install}
          >
            {t('pwa.install.cta')}
          </button>
        )}
        <button
          id="pwa-dismiss-btn"
          type="button"
          className="pwa-banner__close"
          aria-label={t('pwa.install.dismiss')}
          onClick={dismiss}
        >
          ×
        </button>
      </div>
    </div>
  );
}
